/**
 * Create Password component module.
 * @module shared/components/CreatePassword
 *
 */
import React, {useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {passwordStrength} from '../utils/utils';
import {_openModal} from '../reducers/modals';
import {Column, DescriptiveText, Row, TextInput} from '../index';

/**
 * Create Password component
 * @name CreatePassword
 */
const CreatePassword = (props) => {
  const orientationComponents = {
    row: Row,
    column: Column,
  };

  const {dataPoint, disabled, onChange, orientation = 'column', role, unmask, width} = props;

  const type = unmask ? 'text' : 'password';
  const [passwordStrengthMessage] = useState(passwordStrength(null, true, role).message);

  const confirmTemporaryPassword = (passwords, target) => {
    const {confirmed, initial} = passwords;
    if (target === 'confirmed' && (initial && confirmed) && (initial !== confirmed)) {
      return 'Passwords do not match.';
    }

    return passwordStrength(initial, false, role).valid ? '' : 'Make sure your password meets all criteria.';
  };

  const isTempPasswordValid = target => {
    const passwords = {};
    if (target.name === 'password') {
      passwords.initial = target.value;
      passwords.confirmed = dataPoint.confirmed;
    } else {
      passwords.confirmed = target.value;
      passwords.initial = dataPoint.password;
    }
    return confirmTemporaryPassword(passwords, target.name);
  };

  const Display = orientationComponents[orientation];

  const displayOpts = orientation === 'column' ? {
    alignItems: 'left',
    justifySubColumn: 'top',
    layout: {grow: 1},
  } : {
    justifySubRow: 'left',
  };

  const helpIcon = {
    title: 'Password Strength',
    content: <DescriptiveText>{passwordStrengthMessage}</DescriptiveText>,
  };

  const render = () => <Display {...displayOpts}>
    <TextInput title='Enter Password'
               type={type}
               placeholder='Enter Password'
               dataPoint={{name: 'password', value: dataPoint.password}}
               disabled={disabled}
               help={helpIcon}
               onChange={(name, value) => onChange(name, value)}
               validations={{custom: isTempPasswordValid}}
               width={width}
               required/>
    <TextInput title='Confirm Password'
               type={type}
               placeholder='Confirm Password'
               dataPoint={{name: 'confirmed', value: dataPoint.confirmed}}
               dependency={dataPoint.password}
               disabled={disabled}
               onChange={(name, value) => onChange(name, value)}
               validations={{custom: isTempPasswordValid}}
               width={width}
               required/>
  </Display>;

  return render();
};

const mapDispatchToProps = dispatch => ({
  openModal: (modal) => dispatch(_openModal(modal)),
});

export default (connect(null, mapDispatchToProps)(CreatePassword));

// Document ETODO KST-1796 Need to document Create Password
CreatePassword.propTypes = {
  /**
   * Input Data, holds values for password and confirmed.
   */
  dataPoint: PropTypes.shape({
    password: PropTypes.string,
    confirmed: PropTypes.string,
  }).isRequired,
  /**
   * Whether this component is disabled. (optional)
   */
  disabled: PropTypes.bool,
  /**
   * On Change Function
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Function that opens modals throughout page
   */
  openModal: PropTypes.func.isRequired,
  /**
   * Orientation to display fields, expected 'column' or 'row'
   */
  orientation: PropTypes.oneOf(['column', 'row']),
  /**
   * Required if generating password for a specific role
   */
  role: PropTypes.string,
  /**
   * If true, password is visible. Defaults to false.
   */
  unmask: PropTypes.bool,
  /**
   * Width of the Text Input fields, as CSS (e.g. '500px', '50%')
   */
  width: PropTypes.string,
};
